/* Concept dropdowns (DESIGNCONCEPT: white list, light-blue hover, blue selection with check mark).
   Two modes, both keep the native <select> as the source of truth for the form value and the change event:
   1. Button mode, <select data-pfx-select> (homepage hero finder): the select is replaced by a combobox button
      (WAI-ARIA "select-only combobox"); a MutationObserver keeps it in sync when other scripts rebuild the options.
   2. Native mode, every other single select on the site: the select keeps its own styling, focus, validation and
      labels; only the pop-up list is replaced, for mouse and keyboard. Touch input keeps the operating-system picker.
   The list lives in <body> with position: fixed, so clipping containers (hero card, dialogs) do not cut it off.
   Without JavaScript every select works natively. */
(() => {
  if (window.PfxSelect) return;
  let uid = 0;
  const chevron = '<svg class="pfx-select__chev" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';
  const check = '<svg class="pfx-select__check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5 10 17l9-10"/></svg>';

  // Shared: position a fixed list under (or above) an anchor, inside the viewport and above the mobile tab bar.
  function placeList(list, anchor) {
    const rect = anchor.getBoundingClientRect();
    const viewW = document.documentElement.clientWidth, viewH = window.innerHeight;
    const bar = document.querySelector('.pf-tabbar');
    const barH = bar && getComputedStyle(bar).display !== 'none' ? bar.getBoundingClientRect().height : 0;
    list.style.minWidth = rect.width + 'px';
    list.style.maxHeight = '';
    const width = Math.min(list.offsetWidth, viewW - 32);
    const left = Math.max(16, Math.min(rect.left, viewW - 16 - width));
    const below = viewH - barH - rect.bottom - 16, above = rect.top - 16;
    const wanted = Math.min(list.scrollHeight, 320);
    const up = below < Math.min(wanted, 200) && above > below;
    list.style.left = left + 'px';
    list.style.maxHeight = Math.max(140, Math.min(320, up ? above - 8 : below - 8)) + 'px';
    if (up) { list.style.top = ''; list.style.bottom = (viewH - rect.top + 8) + 'px'; }
    else { list.style.bottom = ''; list.style.top = (rect.bottom + 8) + 'px'; }
    list.classList.toggle('is-up', up);
  }

  function scrollIntoList(list, item) {
    if (item.offsetTop < list.scrollTop) list.scrollTop = item.offsetTop - 6;
    else if (item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = item.offsetTop + item.offsetHeight - list.clientHeight + 6;
  }

  function optionItem(option, index, idPrefix, selected) {
    const item = document.createElement('li');
    item.id = idPrefix + index;
    item.className = 'pfx-select__option' + (option.value === '' ? ' is-empty' : '');
    item.setAttribute('role', 'option');
    item.setAttribute('aria-selected', String(selected));
    if (option.disabled || (option.parentElement && option.parentElement.disabled)) item.setAttribute('aria-disabled', 'true');
    item.dataset.index = index;
    item.innerHTML = '<span></span>' + check;
    item.firstChild.textContent = option.label || option.textContent;
    return item;
  }

  function typeahead(state, key, labels, from) {
    const now = Date.now();
    state.typed = (now - state.typedAt < 700 ? state.typed : '') + key.toLowerCase();
    state.typedAt = now;
    const start = from + (state.typed.length === 1 ? 1 : 0);
    for (let step = 0; step < labels.length; step++) {
      const index = (start + step) % labels.length;
      if (labels[index] !== null && labels[index].startsWith(state.typed)) return index;
    }
    return -1;
  }

  /* ---------- 1. Button mode ---------- */
  function enhance(select) {
    if (select.dataset.pfxSelectInit) return;
    select.dataset.pfxSelectInit = '1';
    const id = 'pfx-select-' + (++uid);
    const field = select.closest('[data-pfx-select-field]') || select.parentElement;
    const caption = field.querySelector('[data-pfx-select-caption]');
    if (caption && !caption.id) caption.id = id + '-label';

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.id = id + '-button';
    trigger.className = 'pfx-select__trigger';
    trigger.dataset.pfxSelectTrigger = '';
    trigger.setAttribute('role', 'combobox');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', id + '-list');
    if (caption) trigger.setAttribute('aria-labelledby', caption.id + ' ' + trigger.id);
    trigger.innerHTML = '<span class="pfx-select__value"></span>' + chevron;
    const value = trigger.firstChild;

    const list = document.createElement('ul');
    list.id = id + '-list';
    list.className = 'pfx-select__list';
    list.setAttribute('role', 'listbox');
    list.tabIndex = -1;
    if (caption) list.setAttribute('aria-labelledby', caption.id);
    list.hidden = true;

    select.hidden = true;
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');
    select.after(trigger);
    document.body.appendChild(list);
    if (field.tagName === 'LABEL') field.htmlFor = trigger.id;
    field.classList.add('pfx-select');

    let open = false, active = -1;
    const state = { typed: '', typedAt: 0 };
    const options = () => [...select.options];

    function render() {
      list.replaceChildren(...options().map((option, index) => optionItem(option, index, id + '-option-', option.selected)));
      const current = select.options[select.selectedIndex];
      value.textContent = current ? current.textContent : '';
      if (open) setActive(active);
    }

    function setActive(index) {
      const items = list.children;
      if (!items.length) return;
      index = Math.max(0, Math.min(items.length - 1, index));
      for (const item of items) item.classList.remove('is-active');
      items[index].classList.add('is-active');
      active = index;
      trigger.setAttribute('aria-activedescendant', items[index].id);
      scrollIntoList(list, items[index]);
    }

    function show(index) {
      if (open) return;
      open = true;
      list.hidden = false;
      field.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      placeList(list, field);
      setActive(index == null ? select.selectedIndex : index);
      document.addEventListener('pointerdown', outside, true);
      window.addEventListener('scroll', onScroll, true);
      window.addEventListener('resize', close);
    }

    function hide(focus) {
      if (!open) return;
      open = false;
      list.hidden = true;
      field.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.removeAttribute('aria-activedescendant');
      document.removeEventListener('pointerdown', outside, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
      if (focus) trigger.focus();
    }
    const close = () => hide(false);
    const outside = event => { if (!field.contains(event.target) && !list.contains(event.target)) hide(false); };
    const onScroll = event => { if (event.target !== list) hide(false); };

    function choose(index) {
      const option = select.options[index];
      if (!option || option.disabled) return;
      if (select.selectedIndex !== index) {
        select.selectedIndex = index;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      render();
    }
    const labels = () => options().map(option => option.textContent.trim().toLowerCase());

    trigger.addEventListener('click', () => (open ? hide(true) : show()));
    trigger.addEventListener('keydown', event => {
      const key = event.key, count = options().length;
      const printable = key.length === 1 && key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey;
      if (!open) {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(key)) { event.preventDefault(); show(); }
        else if (key === 'Home') { event.preventDefault(); show(0); }
        else if (key === 'End') { event.preventDefault(); show(count - 1); }
        else if (printable) { const index = typeahead(state, key, labels(), select.selectedIndex); if (index > -1) choose(index); }
        return;
      }
      switch (key) {
        case 'ArrowDown': event.preventDefault(); if (event.altKey) { choose(active); hide(true); } else setActive(active + 1); break;
        case 'ArrowUp': event.preventDefault(); if (event.altKey) { choose(active); hide(true); } else setActive(active - 1); break;
        case 'Home': event.preventDefault(); setActive(0); break;
        case 'End': event.preventDefault(); setActive(count - 1); break;
        case 'PageDown': event.preventDefault(); setActive(active + 10); break;
        case 'PageUp': event.preventDefault(); setActive(active - 10); break;
        case 'Enter': case ' ': event.preventDefault(); choose(active); hide(true); break;
        case 'Escape': event.preventDefault(); hide(true); break;
        case 'Tab': choose(active); hide(false); break;
        default: if (printable) { const index = typeahead(state, key, labels(), active); if (index > -1) setActive(index); }
      }
    });
    list.addEventListener('pointerdown', event => event.preventDefault());
    list.addEventListener('click', event => {
      const item = event.target.closest('[role="option"]');
      if (!item || item.getAttribute('aria-disabled') === 'true') return;
      choose(Number(item.dataset.index));
      hide(true);
    });
    list.addEventListener('pointermove', event => {
      const item = event.target.closest('[role="option"]');
      if (item && Number(item.dataset.index) !== active) setActive(Number(item.dataset.index));
    });
    new MutationObserver(render).observe(select, { childList: true, subtree: true, characterData: true });
    select.addEventListener('change', render);
    render();
  }

  /* ---------- 2. Native mode ---------- */
  const nat = { select: null, list: null, active: -1, original: -1, typed: '', typedAt: 0 };
  let lastPointer = 'mouse';
  const coarse = window.matchMedia('(hover: none) and (pointer: coarse)');
  const isMac = /Mac|iPhone|iPad/.test(navigator.platform || '');
  const eligible = el => el instanceof HTMLSelectElement && !el.multiple && el.size <= 1 && !el.disabled
    && !el.hasAttribute('data-pfx-select') && !el.closest('[data-pfx-native]');

  function natList() {
    if (nat.list) return nat.list;
    const list = document.createElement('ul');
    list.id = 'pfx-select-native-list';
    list.className = 'pfx-select__list';
    list.setAttribute('role', 'listbox');
    list.tabIndex = -1;
    list.hidden = true;
    list.addEventListener('pointerdown', event => event.preventDefault());
    list.addEventListener('click', event => {
      const item = event.target.closest('[role="option"]');
      if (!item || item.getAttribute('aria-disabled') === 'true' || !nat.select) return;
      const select = nat.select;
      select.selectedIndex = Number(item.dataset.index);
      natClose(true);
      select.focus({ preventScroll: true });
    });
    list.addEventListener('pointermove', event => {
      const item = event.target.closest('[role="option"]');
      if (item && Number(item.dataset.index) !== nat.active) natActive(Number(item.dataset.index), false);
    });
    document.body.appendChild(list);
    nat.list = list;
    return list;
  }

  function natRender(select) {
    const list = natList(), items = [];
    const add = option => { if (!option.hidden) items.push(optionItem(option, option.index, 'pfx-select-native-option-', option.index === select.selectedIndex)); };
    for (const child of select.children) {
      if (child.tagName === 'OPTGROUP') {
        const group = document.createElement('li');
        group.className = 'pfx-select__group';
        group.setAttribute('role', 'presentation');
        group.textContent = child.label;
        items.push(group);
        [...child.children].forEach(add);
      } else if (child.tagName === 'OPTION') add(child);
    }
    list.replaceChildren(...items);
    const label = select.labels && select.labels[0];
    if (select.getAttribute('aria-label')) list.setAttribute('aria-label', select.getAttribute('aria-label'));
    else if (label) list.setAttribute('aria-label', label.textContent.trim().replace(/\s+/g, ' '));
    else list.removeAttribute('aria-label');
  }

  const natItems = () => [...nat.list.querySelectorAll('[role="option"]:not([aria-disabled="true"])')];

  // live: also move the select's own value (as a native closed select does), so screen readers announce it.
  function natActive(index, live) {
    const items = natItems();
    if (!items.length) return;
    let item = items.find(el => Number(el.dataset.index) === index);
    if (!item) item = items[0];
    for (const el of nat.list.children) el.classList.remove('is-active');
    item.classList.add('is-active');
    nat.active = Number(item.dataset.index);
    scrollIntoList(nat.list, item);
    if (live && nat.select.selectedIndex !== nat.active) {
      nat.select.selectedIndex = nat.active;
      for (const el of nat.list.querySelectorAll('[role="option"]')) el.setAttribute('aria-selected', String(Number(el.dataset.index) === nat.active));
    }
  }

  function natStep(delta) {
    const items = natItems();
    if (!items.length) return;
    let at = items.findIndex(el => Number(el.dataset.index) === nat.active);
    at = Math.max(0, Math.min(items.length - 1, (at < 0 ? 0 : at) + delta));
    natActive(Number(items[at].dataset.index), true);
  }

  function natOpen(select) {
    if (nat.select) natClose(false);
    nat.select = select;
    nat.original = select.selectedIndex;
    natRender(select);
    const list = nat.list;
    list.hidden = false;
    placeList(list, select);
    select.classList.add('pfx-select-open');
    select.setAttribute('aria-expanded', 'true');
    select.setAttribute('aria-controls', list.id);
    natActive(select.selectedIndex, false);
    window.addEventListener('scroll', natScroll, true);
    window.addEventListener('resize', natResize);
  }

  function natClose(commit) {
    const select = nat.select;
    if (!select) return;
    nat.select = null;
    nat.list.hidden = true;
    select.classList.remove('pfx-select-open');
    select.setAttribute('aria-expanded', 'false');
    window.removeEventListener('scroll', natScroll, true);
    window.removeEventListener('resize', natResize);
    if (!commit) { if (select.selectedIndex !== nat.original) select.selectedIndex = nat.original; return; }
    if (select.selectedIndex !== nat.original) {
      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  const natScroll = event => { if (event.target !== nat.list) natClose(false); };
  const natResize = () => natClose(false);

  document.addEventListener('pointerdown', event => {
    lastPointer = event.pointerType || 'mouse';
    if (nat.select && event.target !== nat.select && !nat.list.contains(event.target)) natClose(false);
  }, true);

  document.addEventListener('mousedown', event => {
    const select = event.target;
    if (event.button !== 0 || !eligible(select)) return;
    if (lastPointer === 'touch' || (lastPointer !== 'mouse' && lastPointer !== 'pen') || coarse.matches) return;
    event.preventDefault();
    if (document.activeElement !== select) select.focus({ preventScroll: true });
    if (nat.select === select) natClose(false); else natOpen(select);
  }, true);

  document.addEventListener('keydown', event => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    const key = event.key;
    if (nat.select === select) {
      const printable = key.length === 1 && key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey;
      switch (key) {
        case 'ArrowDown': case 'ArrowUp':
          event.preventDefault();
          if (event.altKey) { natClose(true); break; }
          natStep(key === 'ArrowDown' ? 1 : -1); break;
        case 'Home': event.preventDefault(); natStep(-1e6); break;
        case 'End': event.preventDefault(); natStep(1e6); break;
        case 'PageDown': event.preventDefault(); natStep(10); break;
        case 'PageUp': event.preventDefault(); natStep(-10); break;
        case 'Enter': case ' ': event.preventDefault(); natActive(nat.active, true); natClose(true); break;
        case 'Escape': event.preventDefault(); event.stopPropagation(); natClose(false); break;
        case 'Tab': natClose(true); break;
        default:
          if (printable) {
            event.preventDefault();
            const labels = [...select.options].map(option => (option.disabled || option.hidden ? null : (option.label || option.textContent).trim().toLowerCase()));
            const index = typeahead(nat, key, labels, nat.active);
            if (index > -1) natActive(index, true);
          }
      }
      return;
    }
    if (!eligible(select)) return;
    const opens = key === ' ' || key === 'Enter' || key === 'F4' || (event.altKey && (key === 'ArrowDown' || key === 'ArrowUp'))
      || (isMac && (key === 'ArrowDown' || key === 'ArrowUp'));
    if (opens) { event.preventDefault(); natOpen(select); }
  }, true);

  document.addEventListener('focusout', event => { if (nat.select && event.target === nat.select) natClose(false); }, true);

  /* ---------- Start ---------- */
  const init = root => (root || document).querySelectorAll('select[data-pfx-select]').forEach(enhance);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  window.PfxSelect = { init, close: () => natClose(false) };
})();
